<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :closable="true"
    :dismissableMask="true"
    :draggable="false"
    class="place-map-modal dialog-full-screen"
    @update:visible="$emit('close')"
    :style="{ width: '90vw', maxWidth: '1000px' }"
  >
    <template #header>
      <div class="modal-header">
        <h3>{{ title }}</h3>
        <div v-if="address" class="address-display">
          <MapPin :size="16" />
          <span>{{ formatAddress(address) }}</span>
        </div>
      </div>
    </template>
    
    <div class="map-container">
      <iframe
        v-if="mapUrl"
        :src="mapUrl"
        width="100%"
        height="500"
        frameborder="0"
        scrolling="no"
        marginheight="0"
        marginwidth="0"
        allowfullscreen
        loading="lazy"
      ></iframe>
      <div v-else class="no-location">
        <MapPin :size="48" />
        <p>No location coordinates available</p>
      </div>
    </div>
    
    <div v-if="hasMetadata" class="place-metadata">
      <div class="metadata-row-inline">
        <div v-if="rating" class="metadata-item-inline">
          <Star :size="20" :fill="'#ff69fc'" :color="'#ff69fc'" />
          <div class="metadata-content">
            <span class="label">Rating</span>
            <span class="value">{{ formatRating(rating) }}</span>
          </div>
        </div>
        
        <div v-if="ratingCount" class="metadata-item-inline">
          <Users :size="20" />
          <div class="metadata-content">
            <span class="label">Reviews</span>
            <span class="value">{{ formatNumber(ratingCount) }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <template #footer>
      <div class="modal-footer">
        <Button
          v-if="location"
          label="Open in Google Maps"
          icon="pi pi-external-link"
          class="p-button-outlined"
          @click="openInGoogleMaps"
        />
        <Button
          label="Close"
          icon="pi pi-times"
          @click="$emit('close')"
          autofocus
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { MapPin, Star, Users } from 'lucide-vue-next'

export default {
  name: 'PlaceMapModal',
  components: {
    Dialog,
    Button,
    MapPin,
    Star,
    Users
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Location'
    },
    location: {
      type: String,
      default: null
    },
    address: {
      type: [String, Object],
      default: null
    },
    rating: {
      type: Number,
      default: null
    },
    ratingCount: {
      type: Number,
      default: null
    }
  },
  emits: ['close'],
  setup(props) {
    const mapUrl = computed(() => {
      if (!props.location) return null
      
      // Parse coordinates from "lat,lng" format
      const coords = props.location.split(',').map(c => c.trim())
      if (coords.length !== 2) return null
      
      const [lat, lng] = coords
      
      // Use OpenStreetMap embed with marker
      // Note: For a more advanced implementation, you could use Leaflet.js library
      return `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01},${lat-0.01},${parseFloat(lng)+0.01},${parseFloat(lat)+0.01}&layer=mapnik&marker=${lat},${lng}`
    })
    
    const hasMetadata = computed(() => {
      return props.rating || props.ratingCount
    })
    
    const formatAddress = (address) => {
      if (typeof address === 'string') {
        return address
      }
      
      if (typeof address === 'object' && address !== null) {
        // If fullAddress is provided, use it
        if (address.fullAddress) {
          return address.fullAddress
        }
        
        // Otherwise, construct from parts
        const parts = []
        if (address.streetAddress) parts.push(address.streetAddress)
        if (address.addressLocality) parts.push(address.addressLocality)
        if (address.addressRegion) parts.push(address.addressRegion)
        if (address.postalCode) parts.push(address.postalCode)
        
        return parts.join(', ')
      }
      
      return ''
    }
    
    const formatRating = (rating) => {
      if (rating === null || rating === undefined) return ''
      
      // If rating is < 1, show as percentage
      if (rating < 1) {
        return `${Math.round(rating * 100)}%`
      }
      
      // Otherwise show as rating out of 5
      return `${rating.toFixed(1)} / 5`
    }
    
    const formatNumber = (num) => {
      if (num === null || num === undefined) return ''
      return num.toLocaleString()
    }
    
    const openInGoogleMaps = () => {
      if (!props.location) return
      
      const coords = props.location.split(',').map(c => c.trim())
      if (coords.length !== 2) return
      
      const [lat, lng] = coords
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank')
    }
    
    return {
      mapUrl,
      hasMetadata,
      formatAddress,
      formatRating,
      formatNumber,
      openInGoogleMaps
    }
  }
}
</script>

<style scoped>
.place-map-modal :deep(.p-dialog-header) {
  border-bottom: 2px solid #ff69fc;
  background: linear-gradient(135deg, rgba(255, 105, 252, 0.05) 0%, rgba(255, 105, 252, 0.02) 100%);
}

.place-map-modal :deep(.p-dialog-footer) {
  border-top: 1px solid rgba(255, 105, 252, 0.2);
  padding: 1rem 1.5rem;
}

.modal-header {
  width: 100%;
}

.modal-header h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: var(--text-primary);
}

.address-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

.address-display svg {
  color: #ff69fc;
  flex-shrink: 0;
}

.map-container {
  width: 100%;
  min-height: 500px;
  background: var(--bg-primary);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.map-container iframe {
  display: block;
}

.no-location {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 500px;
  color: var(--text-secondary);
  gap: 1rem;
}

.no-location svg {
  color: #ff69fc;
  opacity: 0.5;
}

.place-metadata {
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid rgba(255, 105, 252, 0.2);
}

.metadata-row-inline {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.metadata-item-inline {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.metadata-item-inline svg {
  color: #ff69fc;
  flex-shrink: 0;
}

.metadata-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metadata-content .label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metadata-content .value {
  font-size: 1rem;
  color: var(--text-primary);
  font-weight: 500;
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

/* Dark theme: the dim pink border needs strengthening (token-based surfaces
   flip on their own; the header re-tint lives in the non-scoped block below). */
:root.dark-theme .place-metadata {
  border-color: rgba(255, 105, 252, 0.3);
}

/* Responsive design */
@media (max-width: 768px) {
  .place-map-modal {
    width: 95vw !important;
  }
  
  .map-container {
    min-height: 300px;
  }
  
  .map-container iframe {
    height: 300px;
  }
  
  .no-location {
    height: 300px;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .modal-footer button {
    width: 100%;
  }
}
</style>

<style>
/* Dark-theme re-tint for the dialog header (PrimeVue-internal element — must
   live in a non-scoped block keyed on the dialog's custom class). */
:root.dark-theme .place-map-modal .p-dialog-header {
  background: linear-gradient(135deg, rgba(255, 105, 252, 0.1) 0%, rgba(255, 105, 252, 0.05) 100%);
}

@media screen and (max-width: 960px) {
    .place-map-modal {
        width: 100vw !important;
        height: 100% !important;
        max-height: 100% !important;
        margin: 0 !important;
        border-radius: 0 !important;
    }
    
    .place-map-modal .p-dialog-content {
        height: 100% !important;
        border-radius: 0 !important;
    }

    .place-map-modal .p-dialog-header {
      border-top-right-radius: 0 !important;
      border-top-left-radius: 0 !important;
    }
    
    .place-map-modal .p-dialog-footer {
      border-bottom-right-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
    }
}
</style>
